import injectConnatix from './injectConnatix'
import { getAdsSettings, handleAds, VideoAdsOptions } from './connatixAds'
import { createConnatixTrack, handlePlayerTracking } from './connatixTracking'
import { DEFAULT_CUSTOMER_ID, DEFAULT_PLAYER_ID } from './consts'
import type { ConnatixConfig, ConnatixOnRender } from './connatixTypes'
import { waitForVideoAdsSetup } from '@/utils/adEngineEvents'
import { CommunicationService } from '@/utils/communication/communicationService'
import { initTracker } from '@/utils/tracking/videoTracking'
import {
    TIMING_FEATURED_VIDEO_INIT,
    recordAndTrackDifference,
} from '@/utils/tracking/videoTiming'
import { CNX_TIMING_EVENTS, handleTiming } from '@/connatix/connatixTiming'
import { handleFloating } from '@/connatix/connatixFloating'

export interface ConnatixOptions {
    mediaId?: string
    playlistId?: string
    playerId?: string
    customerId?: string
    renderId?: string
    ads?: boolean
}

let connatixRenderId = 0

function createPlayerConfig(
    playerOptions: ConnatixOptions,
    videoAdsOptions: VideoAdsOptions,
): ConnatixConfig {
    const settings = getAdsSettings(videoAdsOptions)

    return {
        playerId: determinePlayerId(playerOptions),
        playlistId: playerOptions.playlistId,
        mediaId: playerOptions.mediaId,
        settings,
    }
}

function createConnatixElement(container: HTMLElement, renderId: string) {
    const connatixElement = document.createElement('div')
    connatixElement.id = renderId
    container.appendChild(connatixElement)

    return connatixElement
}

function renderPlayer(
    customerId: string,
    renderId: string,
    config: ConnatixConfig,
    onRender: ConnatixOnRender,
) {
    new Image().src = `https://capi.elements.video/tr/si?token=${config.playerId}&cid=${customerId}`
    window.cnx?.cmd.push(function () {
        window.cnx?.(config).render(renderId, onRender)
    })
}

function determinePlayerId(options: ConnatixOptions): string {
    const overriddenPlayerId = new URLSearchParams(window.location.search).get(
        'cnxPlayerId',
    )
    return overriddenPlayerId ?? options.playerId ?? DEFAULT_PLAYER_ID
}

export function createConnatixVideoTracker(
    options: ConnatixOptions,
    category?: string,
) {
    const playerDetails = {
        id: determinePlayerId(options),
        provider: 'connatix',
        version: __VIDEO_PLAYER_VERSION__,
    }
    return initTracker(playerDetails, category)
}

export default async function initializeConnatix(
    container: HTMLElement,
    options: ConnatixOptions,
): Promise<(() => void) | undefined> {
    const {
        customerId = DEFAULT_CUSTOMER_ID,
        renderId = `connatix-player-${connatixRenderId++}`,
        ads = true,
    } = options
    const videoTracker = createConnatixVideoTracker(options)

    videoTracker.trackPlayerEligible(renderId)

    const connatixElement = createConnatixElement(container, renderId)

    const videoAdsOptions = ads
        ? await waitForVideoAdsSetup()
        : { showAds: false }

    if (!videoAdsOptions) {
        return
    }

    injectConnatix(customerId)
    recordAndTrackDifference(
        CNX_TIMING_EVENTS.CNX_SCRIPTS_LOAD_START,
        TIMING_FEATURED_VIDEO_INIT,
    )
    videoTracker.trackPlayerLoad(renderId)

    const config = createPlayerConfig(options, videoAdsOptions)

    return new Promise((resolve, reject) => {
        renderPlayer(customerId, renderId, config, (renderError, playerApi) => {
            if (renderError) {
                videoTracker.trackPlayerError(renderId, renderError.type)
                reject(renderError)
                return
            }

            const track = createConnatixTrack(renderId, playerApi, videoTracker)
            const communicationService = CommunicationService.getInstance()

            communicationService.broadcast({
                type: '[Video] Player rendered',
                payload: { renderId, videoAdsOptions },
            })
            videoTracker.trackPlayerReady(renderId)
            recordAndTrackDifference(
                CNX_TIMING_EVENTS.CNX_READY,
                CNX_TIMING_EVENTS.CNX_SCRIPTS_LOAD_START,
            )
            handleAds(playerApi, connatixElement, videoAdsOptions)
            handlePlayerTracking(playerApi, track)
            handleTiming(playerApi)
            handleFloating(playerApi, communicationService)

            resolve(() => playerApi.destroy())
        })
    })
}
